import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  FiPlus,
  FiMinus,
  FiTrash2,
  FiX,
  FiArrowLeft,
} from "react-icons/fi";
import crop2 from "../../assets/logos/crop2.png";
import crop44 from "../../assets/logos/crop44.png";
import milk from "../../assets/logos/milk.jpg";
import crop00 from "../../assets/logos/crop00.jpeg";
import crop88 from "../../assets/logos/crop88.png";
import crop90 from "../../assets/logos/crop90.jpg";
import { ShoppingCart, Check, AlertCircle, Loader } from "lucide-react";
import api from "../../services/api";

// Sample product images for fallback
const productImages = [crop2, crop44, milk, crop00, crop88, crop90];

const getProductImage = (product) => {
  if (product?.image && product.image !== null) {
    return product.image;
  }
  if (product?.product_image && product.product_image !== null) {
    return product.product_image;
  }
  // Use product ID to consistently pick a fallback image
  const id = product?.product_id || product?.id || 0;
  return productImages[id % productImages.length];
};

const getProductName = (product) => {
  return product?.name || product?.product_name || 'Product';
};

// Alert Component
const Alert = ({ type, message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`alert alert-${type}`}>
      <div className="alert-content">
        {type === 'success' ? <Check size={20} /> : <AlertCircle size={20} />}
        <span>{message}</span>
      </div>
      <button onClick={onClose} className="alert-close">
        <FiX size={16} />
      </button>
    </div>
  );
};

// Checkout Modal Component
const CheckoutModal = ({ isOpen, onClose, cart, total, onCheckout }) => {
  const [shippingAddress, setShippingAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!shippingAddress.trim() || !phone.trim()) {
      return;
    }
    setLoading(true);
    const checkoutData = {
      shipping_address: shippingAddress,
      phone: phone
    };
    console.log('Checkout data being sent:', checkoutData);
    await onCheckout(checkoutData);
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content checkout-modal">
        <div className="modal-header">
          <h2>Checkout</h2>
          <button onClick={onClose} className="modal-close">
            <FiX size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div className="checkout-summary">
            <h3>Order Summary</h3>
            {cart.map((item) => (
              <div key={item.cart_id} className="checkout-item">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <img
                    src={getProductImage(item.product)}
                    alt={getProductName(item.product)}
                    style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'cover' }}
                  />
                  <span>{getProductName(item.product)}</span>
                </div>
                <span>{item.quantity} × ₦{parseFloat(item.price).toLocaleString()}</span>
                <span>₦{parseFloat(item.total).toLocaleString()}</span>
              </div>
            ))}
            <div className="checkout-total">
              <strong>Total: ₦{total.toLocaleString()}</strong>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="checkout-form">
            <div className="form-group">
              <label htmlFor="shipping-address">Shipping Address *</label>
              <textarea
                id="shipping-address"
                value={shippingAddress}
                onChange={(e) => setShippingAddress(e.target.value)}
                placeholder="Enter your full shipping address"
                required
                rows={3}
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <input
                type="tel"
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter your phone number"
                required
              />
            </div>

            <div className="modal-actions">
              <button type="button" onClick={onClose} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? <Loader className="animate-spin" size={16} /> : null}
                {loading ? 'Processing...' : 'Proceed to Payment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Payment Overlay Component
const PaymentOverlay = ({ isOpen, onClose, authorizationUrl, onPaymentComplete }) => {
  useEffect(() => {
    if (isOpen && authorizationUrl) {
      console.log('Opening payment window with URL:', authorizationUrl);

      const paymentWindow = window.open(authorizationUrl, '_blank', 'width=500,height=600');

      if (!paymentWindow) {
        console.log('Popup blocked, redirecting in current window');
        window.location.href = authorizationUrl;
        return;
      }

      console.log('Payment window opened successfully');

      const checkClosed = setInterval(() => {
        if (paymentWindow.closed) {
          clearInterval(checkClosed);
          onPaymentComplete();
        }
      }, 1000);

      return () => {
        clearInterval(checkClosed);
        if (paymentWindow && !paymentWindow.closed) {
          paymentWindow.close();
        }
      };
    }
  }, [isOpen, authorizationUrl, onPaymentComplete]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content payment-modal">
        <div className="payment-loading">
          <Loader className="animate-spin" size={32} />
          <h3>Processing Payment...</h3>
          <p>Payment window opened. Please complete your payment...</p>
          <button onClick={onClose} className="btn-secondary">Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default function CartPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cart, setCart] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showPaymentOverlay, setShowPaymentOverlay] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [updatingQuantity, setUpdatingQuantity] = useState({});
  const [deletingFromCart, setDeletingFromCart] = useState({});

  // No productImages here now

  const showAlertMessage = (type, message) => {
    setAlert({ type, message });
  };

  const hideAlert = () => {
    setAlert(null);
  };

  // Check if user is logged in and is a retailer
  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: '/marketplace/cart' } });
      return;
    }

    if (user.role?.toLowerCase() !== 'retailer') {
      showAlertMessage('error', 'Only retailers can access the cart. Please register as a retailer or login with a retailer account.');
      setTimeout(() => {
        navigate('/marketplace');
      }, 2000);
      return;
    }

    fetchCart();
    fetchProducts();
  }, [user, navigate]);

  const fetchProducts = async () => {
    try {
      const response = await api.getRetailerProducts();
      if (response.success) {
        setProducts(response.data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCart = async () => {
    try {
      const response = await api.getRetailerCart();
      if (response.success) {
        setCart(response.data.items || []);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  };

  // No getProductImage here now

  const removeFromCart = async (cartId) => {
    if (deletingFromCart[cartId]) return;

    try {
      setDeletingFromCart(prev => ({ ...prev, [cartId]: true }));

      const response = await api.deleteCartItem(cartId);
      if (response.success) {
        showAlertMessage('success', 'Item removed from cart');
        fetchCart();
      } else {
        showAlertMessage('error', 'Failed to remove item');
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
      showAlertMessage('error', 'Failed to remove item');
    } finally {
      setDeletingFromCart(prev => ({ ...prev, [cartId]: false }));
    }
  };

  const updateCartQuantity = async (cartId, newQuantity, maxQuantity) => {
    if (newQuantity < 1 || newQuantity > maxQuantity) return;
    if (updatingQuantity[cartId]) return;

    try {
      setUpdatingQuantity(prev => ({ ...prev, [cartId]: true }));

      const response = await api.updateCartItem(cartId, newQuantity);
      if (response.success) {
        fetchCart();
      } else {
        showAlertMessage('error', 'Failed to update quantity');
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      showAlertMessage('error', 'Failed to update quantity');
    } finally {
      setUpdatingQuantity(prev => ({ ...prev, [cartId]: false }));
    }
  };

  const getProductAvailableQuantity = (productId) => {
    const product = products.find(p => p.product_id === productId);
    return product ? product.quantity_available : 999;
  };

  const handleCheckout = async (checkoutData) => {
    try {
      const response = await api.checkout(checkoutData);
      console.log('Checkout response:', response);

      if (response.success) {
        const { authorization_url, reference } = response.data;
        console.log('Payment details:', { authorization_url, reference });

        if (!authorization_url) {
          showAlertMessage('error', 'Payment URL not provided');
          return;
        }

        setPaymentUrl(authorization_url);
        setPaymentReference(reference);
        localStorage.setItem('checkout_reference', reference);

        setShowCheckoutModal(false);
        setShowPaymentOverlay(true);

        pollPaymentVerification(reference);
      } else {
        showAlertMessage('error', response.message || 'Checkout failed');
      }
    } catch (error) {
      console.error('Error during checkout:', error);
      showAlertMessage('error', 'Checkout failed');
    }
  };

  const pollPaymentVerification = async (reference) => {
    const maxAttempts = 30;
    let attempts = 0;

    const pollInterval = setInterval(async () => {
      attempts++;

      try {
        const verifyResponse = await api.verifyPayment(reference);

        if (verifyResponse.success) {
          clearInterval(pollInterval);
          localStorage.removeItem('checkout_reference');
          setPaymentReference('');
          setShowPaymentOverlay(false);

          showAlertMessage('success', 'Order placed successfully!');
          setCart([]);
          fetchCart();
          // Redirect to success page
          navigate('/marketplace/success');
        }
      } catch (err) {
        console.error('Payment verification error:', err);
        if (err.response?.status === 400 || err.response?.status === 422) {
          clearInterval(pollInterval);
          localStorage.removeItem('checkout_reference');
          setPaymentReference('');
          setShowPaymentOverlay(false);
          showAlertMessage('error', 'Payment could not be completed. Please try again.');
        }
      }

      if (attempts >= maxAttempts) {
        clearInterval(pollInterval);
        localStorage.removeItem('checkout_reference');
        setPaymentReference('');
        setShowPaymentOverlay(false);
        showAlertMessage('error', 'Payment verification timeout. Please check your order status.');
      }
    }, 10000);
  };

  const handlePaymentComplete = async () => {
    // Handled by polling
  };

  // Check for pending payment verification on component mount
  useEffect(() => {
    const pendingReference = localStorage.getItem('checkout_reference');
    if (pendingReference) {
      setPaymentReference(pendingReference);
      setShowPaymentOverlay(true);
      pollPaymentVerification(pendingReference);
    }
  }, []);

  const totalAmount = cart.reduce((sum, item) => sum + parseFloat(item.total || 0), 0);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <Loader className="animate-spin" size={32} />
        <p>Loading cart...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={hideAlert}
        />
      )}

      <div style={{ marginBottom: '2rem' }}>
        <button
          onClick={() => navigate('/marketplace')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: 'transparent',
            border: '1px solid #ddd',
            borderRadius: '8px',
            cursor: 'pointer',
            marginBottom: '1rem'
          }}
        >
          <FiArrowLeft size={18} />
          Back to Marketplace
        </button>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Shopping Cart</h1>
        <p style={{ color: '#666' }}>{cart.length} {cart.length === 1 ? 'item' : 'items'} in your cart</p>
      </div>

      {cart.length > 0 ? (
        <>
          <div style={{ marginBottom: '2rem' }}>
            {cart.map((item, index) => (
              <div key={item.cart_id} style={{
                display: 'flex',
                gap: '1.5rem',
                padding: '1.5rem',
                border: '1px solid #ddd',
                borderRadius: '12px',
                marginBottom: '1rem',
                background: 'white'
              }}>
                <div style={{ width: '120px', height: '120px', borderRadius: '8px', overflow: 'hidden' }}>
                  <img
                    src={getProductImage(item.product)}
                    alt={getProductName(item.product)}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
                <div style={{ flex: '1' }}>
                  <h3 style={{ marginBottom: '0.5rem', fontSize: '1.2rem' }}>
                    {getProductName(item.product)}
                  </h3>
                  <p style={{ color: '#666', marginBottom: '0.5rem' }}>
                    By {item.product?.farmer?.name || item.product?.farmer?.userName || 'Unknown Farmer'}
                  </p>
                  <p style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#0e4410', marginBottom: '1rem' }}>
                    ₦{parseFloat(item.price).toLocaleString()} each
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', border: '1px solid #ddd', borderRadius: '6px', padding: '4px' }}>
                      <button
                        onClick={() => updateCartQuantity(item.cart_id, item.quantity - 1, getProductAvailableQuantity(item.product?.id))}
                        disabled={item.quantity <= 1 || updatingQuantity[item.cart_id]}
                        style={{
                          padding: '6px 10px',
                          border: 'none',
                          background: 'transparent',
                          cursor: item.quantity <= 1 ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {updatingQuantity[item.cart_id] ? <Loader className="animate-spin" size={12} /> : <FiMinus />}
                      </button>
                      <span style={{ minWidth: '30px', textAlign: 'center' }}>{item.quantity}</span>
                      <button
                        onClick={() => updateCartQuantity(item.cart_id, item.quantity + 1, getProductAvailableQuantity(item.product?.id))}
                        disabled={item.quantity >= getProductAvailableQuantity(item.product?.id) || updatingQuantity[item.cart_id]}
                        style={{
                          padding: '6px 10px',
                          border: 'none',
                          background: 'transparent',
                          cursor: item.quantity >= getProductAvailableQuantity(item.product?.id) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {updatingQuantity[item.cart_id] ? <Loader className="animate-spin" size={12} /> : <FiPlus />}
                      </button>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.cart_id)}
                      disabled={deletingFromCart[item.cart_id]}
                      style={{
                        padding: '8px 12px',
                        background: '#fee2e2',
                        color: '#991b1b',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      {deletingFromCart[item.cart_id] ? <Loader className="animate-spin" size={14} /> : <FiTrash2 size={14} />}
                      Remove
                    </button>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#0e4410' }}>
                    ₦{parseFloat(item.total).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div style={{
            padding: '1.5rem',
            background: '#f9f9f9',
            borderRadius: '12px',
            position: 'sticky',
            bottom: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <span style={{ fontSize: '1.2rem', fontWeight: '500' }}>Total:</span>
              <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#0e4410' }}>
                ₦{totalAmount.toLocaleString()}
              </span>
            </div>
            <button
              onClick={() => setShowCheckoutModal(true)}
              style={{
                width: '100%',
                padding: '14px',
                background: '#0e4410',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Proceed to Checkout
            </button>
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '4rem' }}>
          <ShoppingCart size={64} color="#ccc" style={{ marginBottom: '1rem' }} />
          <h2 style={{ marginBottom: '0.5rem' }}>Your cart is empty</h2>
          <p style={{ color: '#666', marginBottom: '2rem' }}>Add products from the marketplace</p>
          <button
            onClick={() => navigate('/marketplace')}
            style={{
              padding: '12px 24px',
              background: '#0e4410',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Browse Marketplace
          </button>
        </div>
      )}

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={showCheckoutModal}
        onClose={() => setShowCheckoutModal(false)}
        cart={cart}
        total={totalAmount}
        onCheckout={handleCheckout}
      />

      {/* Payment Overlay */}
      <PaymentOverlay
        isOpen={showPaymentOverlay}
        onClose={() => setShowPaymentOverlay(false)}
        authorizationUrl={paymentUrl}
        onPaymentComplete={handlePaymentComplete}
      />
    </div>
  );
}
